/**
 * QuoteFormWrapper
 *
 * Orchestrates the Request Quote modal's HubSpot integration with a fallback
 * path for users whose browsers block HubSpot's V4 embed loader (ad blockers,
 * corporate firewalls, CSP):
 *
 *   - On mount, render HubSpotQuoteForm and start a heartbeat timer. If
 *     `hs-form-event:on-ready` fires within HEARTBEAT_MS the embed is alive.
 *     If the timer fires first we assume blocked, swap to the local QuoteForm,
 *     and submit via HubSpot's Forms API (served from a different domain than
 *     the embed loader, so often passes ad-blocker filters).
 *
 *   - On the modal's Submit click (signalled by `state.ui.submitRequest`),
 *     check the design has been saved at least once; if not, toast and abort.
 *     Otherwise hand control to the active form path (HubSpot embed or
 *     Forms-API fallback).
 *
 *   - On successful submission (HubSpot or Forms API), mark the quote
 *     requested, close the modal, and show a success toast.
 *
 *   - On submission failure, show an error toast and leave the modal open
 *     so the user can retry.
 */
import { useEffect, useRef, useState, FC } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import HubSpotQuoteForm, { HubSpotQuoteFormHandle } from './HubSpotQuoteForm';
import QuoteForm, { QuoteFormHandle } from './QuoteForm';
import { buildQuoteMessage } from '../../services/hubSpotEquipmentList';
import { submitQuoteToHubSpotFormsApi } from '../../services/hubSpotFormSubmit';
import { getCurrentValue } from 'reduxModules/ducks/container';
import ReducersIndex from 'reduxModules/ducks';
import { useToast } from '../../contexts/ToastContext';

// How long to wait for HubSpot's `on-ready` event before assuming the embed
// loader is blocked and swapping to the Forms-API fallback. 5s comfortably
// covers a slow first-time load on a fresh page.
const HEARTBEAT_MS = 5000;

const QuoteFormWrapper: FC = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { showSuccess, showError } = useToast();
  const hubSpotFormRef = useRef<HubSpotQuoteFormHandle>(null);
  const quoteFormRef = useRef<QuoteFormHandle>(null);
  // Tracks mount status so post-API Promise dispatches don't fire after the
  // user closes the modal mid-flight (the resolution would otherwise produce
  // a stray success toast for an abandoned request).
  const mountedRef = useRef(true);

  const [hubSpotBlocked, setHubSpotBlocked] = useState(false);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const submitRequest = useSelector((state: any) => state.ui?.submitRequest);

  // Heartbeat: if HubSpot doesn't report ready within HEARTBEAT_MS, assume the
  // embed loader is blocked and fall back to the Forms API path.
  useEffect(() => {
    const handleReady = () => {
      clearTimeout(timer);
      window.removeEventListener('hs-form-event:on-ready', handleReady);
    };
    window.addEventListener('hs-form-event:on-ready', handleReady);
    const timer = window.setTimeout(() => {
      console.warn(
        `HubSpot embed did not load within ${HEARTBEAT_MS}ms — falling back to Forms API submission path`,
      );
      setHubSpotBlocked(true);
    }, HEARTBEAT_MS);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('hs-form-event:on-ready', handleReady);
    };
  }, []);

  const handleSubmitSuccess = () => {
    dispatch(ReducersIndex.container.setRequestedQuote(true));
    dispatch(ReducersIndex.uiReducers.closeModal('requestQuote'));
    dispatch(ReducersIndex.uiReducers.clearModals([]));
    showSuccess('Quote request sent');
  };

  const handleSubmitError = () => {
    showError('Quote request failed. Please try again.');
  };

  useEffect(() => {
    if (!submitRequest) return;
    const state = store.getState();

    // Block submission if the design has never been saved. Auto-save covers
    // the "has-savekey-but-unsaved-changes" gap, so no explicit save trigger.
    if (!getCurrentValue(state, 'savekey')) {
      showError('Please save your design before requesting a quote.');
      dispatch(ReducersIndex.uiReducers.setSubmitRequest(false));
      return;
    }

    if (hubSpotBlocked) {
      // Fallback path: validate locally, then POST to HubSpot's Forms API.
      const form = quoteFormRef.current;
      if (!form) {
        dispatch(ReducersIndex.uiReducers.setSubmitRequest(false));
        return;
      }
      if (!form.validate()) {
        // QuoteForm.validate() already showed inline + summary toast errors
        dispatch(ReducersIndex.uiReducers.setSubmitRequest(false));
        return;
      }
      // Keep `submitRequest` true while the request is in flight so a second
      // click on the modal's Submit button is a no-op (Redux ignores
      // same-value updates and the effect doesn't re-fire). The flag is
      // released inside .finally() once the API settles, regardless of
      // success or failure.
      const message = buildQuoteMessage(state);
      submitQuoteToHubSpotFormsApi({ ...form.getFormData(), message })
        .then(() => {
          if (!mountedRef.current) return;
          console.log('Quote request successfully submitted via Forms API');
          handleSubmitSuccess();
        })
        .catch(err => {
          if (!mountedRef.current) return;
          console.error('Forms API submission failed', err);
          handleSubmitError();
        })
        .finally(() => {
          if (!mountedRef.current) return;
          dispatch(ReducersIndex.uiReducers.setSubmitRequest(false));
        });
      return;
    }

    // Embed path: HubSpot runs its own validation on the click. Reset the
    // flag synchronously — HubSpot's click handler is synchronous on our
    // side; any subsequent state changes ride on its event callbacks.
    hubSpotFormRef.current?.submit(buildQuoteMessage(state));
    dispatch(ReducersIndex.uiReducers.setSubmitRequest(false));
    // showSuccess/showError captured via the handlers above; eslint deps OK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitRequest, hubSpotBlocked, dispatch, store, showError]);

  if (hubSpotBlocked) {
    return <QuoteForm formRef={quoteFormRef} />;
  }

  return (
    <HubSpotQuoteForm
      formRef={hubSpotFormRef}
      onSubmitSuccess={handleSubmitSuccess}
      onSubmitError={handleSubmitError}
    />
  );
};

export default QuoteFormWrapper;
